import {
	ReactElement,
	Fragment,
	useState,
	useRef,
	RefObject,
	useEffect
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import classNames from "classnames";
import { useResizeObserver } from "client/Hooks";
import styles from "./ExpandableText.module.less";
import {
	ensureNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import { Guid, newGuid } from "client_legacy/ndaw/guid";

type MaxLines = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

type StringVersion = {
	text: string;
	preserveBreaks?: boolean;
};

type ElementVersion = {
	text: ReactElement;
};

export type ExpandableTextProps = {
	maxLines?: MaxLines;
	trackingId?: string;
	shouldFadeWhenTruncated?: boolean;
} & (StringVersion | ElementVersion);

export const ExpandableText = (props: ExpandableTextProps): ReactElement => {
	const {
		maxLines = 4,
		trackingId,
		shouldFadeWhenTruncated = false
	} = props;

	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isTruncationNeeded, setIsTruncationNeeded] = useState<boolean>(true);
	const [contentId] = useState<Guid>(() => newGuid());

	const contentRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const contentInnerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { height, width } = useResizeObserver(contentInnerRef);
	const isVisible: boolean = width > 0;

	const hasFading: boolean = shouldFadeWhenTruncated && isTruncationNeeded;

	// For better performance we use useEffect instead of
	// useLayoutEffect, because it blocks browser from
	// repainting
	useEffect(
		(): void => {
			hasImplicitDependencyOn(maxLines);
			const contentElement: HTMLDivElement | null = ensureNotNil(contentRef.current);

			if (!isVisible || isExpanded) {
				return;
			}

			const heightWithTruncatedClass: number = contentElement.clientHeight;
			const isTruncationNeeded: boolean =
				height > heightWithTruncatedClass;

			setIsTruncationNeeded(isTruncationNeeded);
		},
		[
			height,
			isVisible,
			isExpanded,
			maxLines
		]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={
				classNames(
					styles.root,
					{
						[styles.root_isTruncated]: !isExpanded
					}
			)}
			style={{
				"--_expandable-text-faded-lines": hasFading ? 1 : 0,
				"--_expandable-text-max-truncated-lines": maxLines
			}}
		>
			<div
				{...setTrackingId("text-content")}
				className={styles.content}
				ref={contentRef}
				id={contentId}
			>
				<div ref={contentInnerRef}>
					<ExpandableTextContent {...props} />
				</div>
			</div>
			{!isExpanded && hasFading && (
				<div className={styles.fadeOverlay}>
					<ExpandableTextContent {...props} />
				</div>
			)}
			{isExpanded
				? (
					<ButtonBase
						trackingId="show-less"
						onClick={() => setIsExpanded(false)}
						aria-expanded
						aria-controls={contentId}
						className={styles.button}
					>
						{localize("_ViewLess")}
					</ButtonBase>
				)
				: isTruncationNeeded && (
					<ButtonBase
						trackingId="show-more"
						onClick={() => setIsExpanded(true)}
						aria-expanded={false}
						aria-controls={contentId}
						className={styles.button}
					>
						{localize("_ViewMore")}
					</ButtonBase>
				)
			}
		</div>
	);
};

ExpandableText.displayName = "ExpandableText";

type ExpandableTextContentProps = StringVersion | ElementVersion;

const ExpandableTextContent = (props: ExpandableTextContentProps): ReactElement => {

	if (isStringVersion(props)) {
		const lines: readonly string[] = !(props.preserveBreaks ?? true)
			? [props.text]
			: props.text.trim()
				.split("\n")
				.map(x => x.trim());

		return (
			<>
				{lines.map((x, i) => (
					<Fragment key={i}>
						{x}<br />
					</Fragment>
				))}
			</>
		);
	} else {
		return props.text;
	}
};
ExpandableTextContent.displayName = "ExpandableTextContent";

function isStringVersion(value: StringVersion | ElementVersion): value is StringVersion {
	return typeof value.text === "string";
}
